import { useState, useCallback, useRef, useMemo, useEffect, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ModuleRegistry,
  themeQuartz,
  type CellStyle,
  type ColDef,
  type GridApi,
  type GridReadyEvent,
  type ICellRendererParams,
  type IGetRowsParams,
  type RowClickedEvent,
  type ValueFormatterParams,
} from 'ag-grid-community';
import { Search } from 'lucide-react';
import clsx from 'clsx';
import { supabase } from '../../lib/supabase';
import { useApp } from '../../hooks/useApp';
import { SELECTION_COLUMN_DEF, emptyFormatter } from '../../lib/gridUtils';
import { reportDatasourceFailure } from '../../lib/agGridErrorTracker';
import ExportButton from '../../components/ui/ExportButton';
import { Toast, type ToastState } from '../../components/ui/Toast';
import ResolveDiscrepancyModal, {
  CONDITION_BADGES,
  RESOLUTION_BADGES,
  type ResolveItemSummary,
} from '../../components/purchasing/ResolveDiscrepancyModal';
import { extractEnvelope, extractBulkEnvelope } from '../../lib/extractEnvelope';
import type { PostgrestError } from '@supabase/supabase-js';

ModuleRegistry.registerModules([AllCommunityModule]);

// Loose RPC caller for get_discrepancy_queue and resolve_discrepancy. Day 84.6 P4:
// error type widened to PostgrestError | null to compose with extractEnvelope.
type LooseRpc = (
  name: string,
  args?: Record<string, unknown>
) => PromiseLike<{ data: unknown; error: PostgrestError | null }>;
const callRpc = supabase.rpc.bind(supabase) as unknown as LooseRpc;

// ── Formatting ──────────────────────────────────────────────────────────────

function relativeTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

// Resolution + condition badge tables and the resolve modal itself live in
// `components/purchasing/ResolveDiscrepancyModal.tsx` so the Discrepancies
// queue and the new Discrepancy detail page (Day 50 Phase 2) stay in sync.

interface DiscrepancyRow {
  id: string;
  po_id: string;
  po_number: string;
  distributor_name: string | null;
  isg_sku: string | null;
  product_title: string | null;
  expected_qty: number | string;
  received_qty: number | string;
  condition: string;
  received_at: string | null;
  resolution: string | null;
  total_count: number;
}

// ── Discrepancies page ──────────────────────────────────────────────────────

export default function Discrepancies(): JSX.Element {
  // Day 64.5 V-ROUI: viewer hides Resolve affordances (ActionsCell button +
  // bulk Resolve bar + ConfirmDialog mount). dist_rep keeps them per
  // Architecture Rule ("dist_rep ✓ scoped" on Purchasing — Discrepancies).
  const { isViewer, isDistRep, profileDistributorId } = useApp();
  const navigate = useNavigate();
  const gridRef = useRef<AgGridReact<DiscrepancyRow> | null>(null);
  const gridApiRef = useRef<GridApi<DiscrepancyRow> | null>(null);

  const [searchText, setSearchText] = useState<string>('');
  const [resolutionFilter, setResolutionFilter] = useState<string>('all');
  const [totalCount, setTotalCount] = useState<number>(0);
  const [toast, setToast] = useState<ToastState | null>(null);

  const [resolveItem, setResolveItem] = useState<DiscrepancyRow | null>(null);
  const [resolving, setResolving] = useState<boolean>(false);
  const [selectedRows, setSelectedRows] = useState<DiscrepancyRow[]>([]);
  const [bulkResolveOpen, setBulkResolveOpen] = useState<boolean>(false);
  const [bulkResolving, setBulkResolving] = useState<boolean>(false);

  const stateRef = useRef<{
    searchText: string;
    resolutionFilter: string;
    _isDistRep: boolean;
    _distributorId: string | null;
  }>({
    searchText,
    resolutionFilter,
    _isDistRep: isDistRep,
    _distributorId: profileDistributorId,
  });
  stateRef.current = {
    searchText,
    resolutionFilter,
    _isDistRep: isDistRep,
    _distributorId: profileDistributorId,
  };

  const getRowsFn = useCallback(async (params: IGetRowsParams): Promise<void> => {
    const { startRow, endRow, successCallback, failCallback } = params;
    const { searchText: st, resolutionFilter: rf } = stateRef.current;

    try {
      const rpcParams: Record<string, unknown> = {
        p_search: st.trim() || null,
        p_resolution: rf === 'all' ? null : rf,
        p_limit: endRow - startRow,
        p_offset: startRow,
      };
      if (stateRef.current._isDistRep && stateRef.current._distributorId) {
        rpcParams.p_distributor_id = stateRef.current._distributorId;
      }
      const { data, error } = await callRpc('get_discrepancy_queue', rpcParams);

      if (error) throw new Error(error.message);

      const rows = (data ?? []) as DiscrepancyRow[];
      const count = rows.length > 0 ? Number(rows[0].total_count) : 0;
      setTotalCount(count);
      successCallback(rows, count);
    } catch (e) {
      reportDatasourceFailure(
        'Discrepancies',
        { rpcName: 'get_discrepancy_queue', params: { startRow, endRow } },
        e,
        () => failCallback()
      );
    }
  }, []);

  const onGridReady = useCallback(
    (event: GridReadyEvent<DiscrepancyRow>): void => {
      gridApiRef.current = event.api;
      event.api.setGridOption('datasource', { getRows: getRowsFn });
    },
    [getRowsFn]
  );

  useEffect(() => {
    if (gridApiRef.current) {
      gridApiRef.current.setGridOption('datasource', { getRows: getRowsFn });
    }
  }, [searchText, resolutionFilter, getRowsFn]);

  const refreshGrid = useCallback((): void => {
    if (gridApiRef.current) {
      gridApiRef.current.setGridOption('datasource', { getRows: getRowsFn });
    }
  }, [getRowsFn]);

  const copyToClipboard = useCallback((text: string | null): void => {
    if (text) navigator.clipboard.writeText(text);
  }, []);

  // ── Cell renderers ────────────────────────────────────────

  type RowParams = ICellRendererParams<DiscrepancyRow>;

  const SkuCell = useCallback(
    (p: RowParams) => {
      if (!p.data) return null;
      return (
        <button
          onClick={(e) => {
            e.stopPropagation();
            copyToClipboard(p.data!.isg_sku);
          }}
          className="font-mono text-accent hover:text-accent-hover transition-colors cursor-pointer"
          title="Click to copy"
        >
          {p.data.isg_sku}
        </button>
      );
    },
    [copyToClipboard]
  );

  const PONumberCell = useCallback(
    (p: RowParams) => {
      if (!p.data) return null;
      return (
        <button
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/purchasing/receiving/${p.data!.po_id}`);
          }}
          className="font-mono text-accent hover:text-accent-hover transition-colors cursor-pointer"
        >
          {p.data.po_number}
        </button>
      );
    },
    [navigate]
  );

  const DiscrepancyCell = useCallback((p: RowParams) => {
    if (!p.data) return null;
    const { expected_qty, received_qty, condition } = p.data;
    if (condition !== 'new') {
      const badge = CONDITION_BADGES[condition] || {
        label: condition,
        cls: 'bg-surface-4 text-ink-muted',
      };
      return (
        <span className={clsx('px-1.5 py-0.5 rounded text-2xs font-medium', badge.cls)}>
          {badge.label}
        </span>
      );
    }
    return (
      <span className="px-1.5 py-0.5 rounded text-2xs font-medium bg-danger/10 text-danger">
        Expected {expected_qty} / Got {received_qty}
      </span>
    );
  }, []);

  const ConditionCell = useCallback((p: RowParams) => {
    if (!p.data) return null;
    const badge = CONDITION_BADGES[p.data.condition] || {
      label: p.data.condition,
      cls: 'bg-surface-4 text-ink-muted',
    };
    return (
      <span className={clsx('px-1.5 py-0.5 rounded text-2xs font-medium', badge.cls)}>
        {badge.label}
      </span>
    );
  }, []);

  const ResolutionCell = useCallback((p: RowParams) => {
    if (!p.data) return null;
    if (!p.data.resolution) {
      return (
        <span className="px-1.5 py-0.5 rounded text-2xs font-medium bg-danger/10 text-danger">
          Unresolved
        </span>
      );
    }
    const badge = RESOLUTION_BADGES[p.data.resolution] || {
      label: p.data.resolution,
      cls: 'bg-surface-4 text-ink-muted',
    };
    return (
      <span className={clsx('px-1.5 py-0.5 rounded text-2xs font-medium', badge.cls)}>
        {badge.label}
      </span>
    );
  }, []);

  const ActionsCell = useCallback(
    (p: RowParams) => {
      if (!p.data || p.data.resolution) return null;
      if (isViewer) return null;
      return (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setResolveItem(p.data!);
          }}
          className="arbi-btn-ghost text-2xs px-2 py-0.5 cursor-pointer"
        >
          Resolve
        </button>
      );
    },
    [isViewer]
  );

  // ── Resolve handler ──────────────────────────────────────
  const handleResolve = async (resolution: string, notes: string): Promise<void> => {
    if (!resolveItem) return;
    setResolving(true);
    try {
      // Day 84.6 P4.2.b — resolve_discrepancy + create_entity_note envelope-extract
      // (mirror of DiscrepancyDetail.tsx fix). Pre-Phase-1 returned resolution uuid;
      // post-migration returns { ok, data: { resolution_id }, userErrors } envelope.
      // The `resolutionId as string` cast was writing envelope object as
      // entity_notes FK — corrupted writes silently.
      const resolveResult = await callRpc('resolve_discrepancy', {
        p_line_item_id: resolveItem.id,
        p_resolution: resolution,
      });
      const resolveEnv = extractEnvelope<{ resolution_id: string }>(resolveResult);
      if (!resolveEnv.isOk) {
        throw new Error(resolveEnv.userErrors[0]?.message ?? 'Resolve failed');
      }
      const resolutionId = resolveEnv.data!.resolution_id;
      // Capture the optional rationale into entity_notes (replaces former
      // discrepancy_resolutions.notes scalar write).
      if (notes.trim() && resolutionId) {
        const noteResult = await callRpc('create_entity_note', {
          p_entity_type: 'discrepancy_resolution',
          p_entity_id: resolutionId,
          p_text: notes.trim(),
          p_visibility: 'all_internal',
        });
        const noteEnv = extractEnvelope<{ id: string }>(noteResult);
        if (!noteEnv.isOk) {
          // Note write failure is non-blocking — log + continue (resolution succeeded)
          console.warn('[Discrepancies] note write failed:', noteEnv.userErrors);
        }
      }
      setToast({ kind: 'success', message: 'Discrepancy resolved' });
      setResolveItem(null);
      refreshGrid();
    } catch (e) {
      console.error('[Discrepancies] resolve error:', e);
      setToast({
        kind: 'error',
        message: `Failed to resolve: ${e instanceof Error ? e.message : String(e)}`,
      });
    } finally {
      setResolving(false);
    }
  };

  const handleBulkResolve = async (resolution: string, notes: string): Promise<void> => {
    const unresolved = selectedRows.filter((r) => !r.resolution);
    if (unresolved.length === 0) return;
    if (unresolved.length > 1000) {
      setToast({
        kind: 'error',
        message: 'Selection exceeds 1000-item bulk cap. Narrow the selection.',
      });
      return;
    }
    setBulkResolving(true);
    // Day 84.6 P4.3 — bulk_resolve_discrepancies per-row envelope-extract per Q4
    // ratification. Pre-Phase-1 returned TABLE(line_item_id, resolution_id);
    // post-migration returns TABLE(line_item_id, ok, data, user_errors) per-row
    // envelope. `data.length` was misleading on partial failures.
    const bulkResult = await callRpc('bulk_resolve_discrepancies', {
      p_line_item_ids: unresolved.map((r) => r.id),
      p_resolution: resolution,
      p_notes: notes.trim() || null,
    });
    setBulkResolving(false);
    setBulkResolveOpen(false);
    const result = extractBulkEnvelope<{ resolution_id: string }, 'line_item_id'>(bulkResult);
    if (!result.isOk && result.rows.length === 0) {
      setToast({
        kind: 'error',
        message: `Bulk resolve failed: ${bulkResult.error?.message ?? 'unknown'}`,
      });
      return;
    }
    const successCount = result.rows.filter((r) => r.ok).length;
    const failedCount = result.rows.length - successCount;
    if (result.hasAnyFailure) {
      const failedDetails = result.rows
        .filter((r) => !r.ok)
        .map((r) => ({
          line_item_id: r.line_item_id,
          code: r.user_errors[0]?.code,
          message: r.user_errors[0]?.message,
        }));
      console.warn('[Discrepancies] bulk-resolve partial-failure:', failedDetails);
      setToast({
        kind: 'error',
        message: `${successCount} of ${result.rows.length} discrepancies resolved; ${failedCount} failed`,
      });
    } else {
      setToast({
        kind: 'success',
        message: `${successCount} discrepanc${successCount !== 1 ? 'ies' : 'y'} resolved`,
      });
    }
    setSelectedRows([]);
    if (gridApiRef.current) {
      gridApiRef.current.deselectAll();
    }
    refreshGrid();
  };

  const onSelectionChanged = useCallback((): void => {
    if (gridApiRef.current) {
      setSelectedRows(gridApiRef.current.getSelectedRows());
    }
  }, []);

  const onRowClicked = useCallback(
    (e: RowClickedEvent<DiscrepancyRow>): void => {
      if (!e.data) return;
      navigate(`/purchasing/discrepancies/${e.data.id}`);
    },
    [navigate]
  );

  const columnDefs = useMemo<ColDef<DiscrepancyRow>[]>(
    () => [
      {
        headerName: 'ISG SKU',
        field: 'isg_sku',
        minWidth: 120,
        suppressMovable: true,
        cellRenderer: SkuCell,
        cellStyle: { display: 'flex', alignItems: 'center' } as CellStyle,
      },
      {
        headerName: 'PO Number',
        field: 'po_number',
        minWidth: 130,
        cellRenderer: PONumberCell,
        cellStyle: { display: 'flex', alignItems: 'center' } as CellStyle,
      },
      {
        headerName: 'Distributor',
        field: 'distributor_name',
        flex: 1,
        minWidth: 120,
        cellStyle: { display: 'flex', alignItems: 'center' } as CellStyle,
      },
      {
        headerName: 'Product',
        field: 'product_title',
        minWidth: 180,
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          fontWeight: 500,
          color: 'var(--ink-bright)',
        } as CellStyle,
        tooltipField: 'product_title',
        valueFormatter: (p: ValueFormatterParams<DiscrepancyRow>) =>
          p.value ? String(p.value) : '—',
      },
      {
        headerName: 'Discrepancy',
        colId: '_discrepancy',
        minWidth: 130,
        sortable: false,
        cellRenderer: DiscrepancyCell,
        cellStyle: { display: 'flex', alignItems: 'center' } as CellStyle,
      },
      {
        headerName: 'Condition',
        field: 'condition',
        minWidth: 110,
        cellRenderer: ConditionCell,
        cellStyle: { display: 'flex', alignItems: 'center' } as CellStyle,
      },
      {
        headerName: 'Received At',
        field: 'received_at',
        minWidth: 120,
        valueFormatter: (p: ValueFormatterParams<DiscrepancyRow>) =>
          relativeTime(p.value as string | null),
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          fontFamily: "'JetBrains Mono', monospace",
          color: 'var(--ink-muted)',
        } as CellStyle,
      },
      {
        headerName: 'Resolution',
        field: 'resolution',
        minWidth: 130,
        cellRenderer: ResolutionCell,
        cellStyle: { display: 'flex', alignItems: 'center' } as CellStyle,
      },
      {
        headerName: 'Actions',
        colId: '_actions',
        minWidth: 90,
        maxWidth: 100,
        sortable: false,
        filter: false,
        cellRenderer: ActionsCell,
        cellStyle: { display: 'flex', alignItems: 'center' } as CellStyle,
      },
    ],
    [SkuCell, PONumberCell, DiscrepancyCell, ConditionCell, ResolutionCell, ActionsCell]
  );

  const defaultColDef = useMemo<ColDef<DiscrepancyRow>>(
    () => ({
      sortable: true,
      resizable: true,
      sortingOrder: ['asc', 'desc', null],
      unSortIcon: true,
      headerClass: 'ag-left-aligned-header',
      cellStyle: { textAlign: 'left' },
      valueFormatter: emptyFormatter,
    }),
    []
  );

  const gridTheme = useMemo(
    () =>
      themeQuartz.withParams({
        backgroundColor: 'var(--surface-2)',
        headerBackgroundColor: 'var(--surface-3)',
        oddRowBackgroundColor: 'var(--surface-2)',
        rowHoverColor: 'var(--surface-3)',
        selectedRowBackgroundColor: 'var(--accent-muted)',
        borderColor: 'var(--border)',
        headerTextColor: 'var(--ink-muted)',
        textColor: 'var(--ink-primary)',
        rowBorder: { color: 'var(--border-subtle)', width: 1, style: 'solid' },
        fontFamily: "'Inter', system-ui, sans-serif",
        fontSize: '12px',
        headerHeight: 36,
        rowHeight: 42,
        spacing: 4,
      }),
    []
  );

  const hasActiveFilters = searchText.trim() || resolutionFilter !== 'all';

  const bulkSummary: ResolveItemSummary | null = bulkResolveOpen
    ? {
        isg_sku: `${selectedRows.filter((r) => !r.resolution).length} discrepancies`,
        expected_qty: '—',
        received_qty: '—',
        condition: '—',
      }
    : null;

  return (
    <div className="flex flex-col h-full -m-4">
      <Toast toast={toast} onDismiss={() => setToast(null)} />
      <ResolveDiscrepancyModal
        open={!!resolveItem}
        item={resolveItem}
        onConfirm={handleResolve}
        onCancel={() => setResolveItem(null)}
        submitting={resolving}
      />
      <ResolveDiscrepancyModal
        open={bulkResolveOpen}
        item={bulkSummary}
        onConfirm={handleBulkResolve}
        onCancel={() => setBulkResolveOpen(false)}
        submitting={bulkResolving}
      />

      {/* Toolbar */}
      <div className="flex-shrink-0 px-4 py-2 bg-surface-1 border-b border-border">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <h1 className="text-base font-semibold text-ink-bright">Discrepancies</h1>
            <span className="text-xs text-ink-muted">
              {totalCount > 0 ? `${totalCount} discrepanc${totalCount !== 1 ? 'ies' : 'y'}` : ''}
            </span>
            {selectedRows.length > 0 && selectedRows.some((r) => !r.resolution) && !isViewer && (
              <div className="flex items-center gap-2 ml-2">
                <span className="text-xs text-ink-muted">{selectedRows.length} selected</span>
                <button
                  onClick={() => setBulkResolveOpen(true)}
                  className="arbi-btn-primary text-2xs px-2 py-1"
                >
                  Resolve Selected
                </button>
              </div>
            )}
          </div>
          <ExportButton gridApiRef={gridApiRef} fileName="arbi-discrepancies" />
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-surface-2 rounded-md p-0.5 gap-0.5 border border-border-subtle">
            {(
              [
                { id: 'all', label: 'All' },
                { id: 'unresolved', label: 'Unresolved' },
                { id: 'accept_shortage', label: 'Accepted' },
                { id: 'request_replacement', label: 'Replacement' },
                { id: 'return_to_distributor', label: 'Return' },
                { id: 'write_off', label: 'Write Off' },
              ] as const
            ).map((f) => (
              <button
                key={f.id}
                onClick={() => setResolutionFilter(f.id)}
                className={clsx(
                  'px-2 py-0.5 text-2xs font-medium rounded transition-colors cursor-pointer',
                  resolutionFilter === f.id
                    ? 'bg-surface-4 text-ink-bright'
                    : 'text-ink-muted hover:text-ink-secondary'
                )}
              >
                {f.label}
              </button>
            ))}
          </div>

          <div className="relative ml-auto">
            <Search
              size={13}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-muted pointer-events-none"
            />
            <input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search SKU, PO, distributor..."
              aria-label="Search discrepancies"
              className="arbi-input pl-8 pr-3 py-1 text-xs w-60"
            />
          </div>
        </div>
      </div>

      {isDistRep && !profileDistributorId && (
        <div className="px-4 py-3 bg-danger/10 border-b border-danger/30 text-sm text-danger">
          Your account is not assigned to a distributor — contact your administrator.
        </div>
      )}

      <div className="flex-1 p-2" style={{ minHeight: 0 }}>
        <AgGridReact<DiscrepancyRow>
          ref={gridRef}
          theme={gridTheme}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          rowModelType="infinite"
          cacheBlockSize={200}
          maxBlocksInCache={50}
          selectionColumnDef={SELECTION_COLUMN_DEF}
          rowSelection={{
            mode: 'multiRow',
            enableClickSelection: false,
            checkboxes: true,
            headerCheckbox: false,
          }}
          onGridReady={onGridReady}
          onSelectionChanged={onSelectionChanged}
          onRowClicked={onRowClicked}
          animateRows={false}
          headerHeight={36}
          rowHeight={42}
          suppressCellFocus={true}
          suppressHorizontalScroll={true}
          overlayNoRowsTemplate={
            hasActiveFilters
              ? '<span style="color:var(--ink-muted);font-size:13px">No discrepancies match your filters</span>'
              : '<span style="color:var(--ink-muted);font-size:13px">No discrepancies — discrepancies appear here when receiving reveals mismatches</span>'
          }
        />
      </div>
    </div>
  );
}
